import { GridSortDirection } from "@mui/x-data-grid";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { AccountExtended, AccountWithAuthz } from "../types/account";
import { SecLevelEndpoint, Webmap as IWebMap, HEADERS } from "./portal";
const API_URL = import.meta.env.VITE_WEBMAP_BACKEND_URL;
const API_VERSION = import.meta.env.VITE_WEBMAP_BACKEND_VERSION;

const axiosInstance = axios.create({
  baseURL: API_URL + `/api/${API_VERSION}`,
  headers: HEADERS,
  withCredentials: true, // send cookies
});

/**
 * Get own project specific account info. We assume that the user has previously logged in via the portal
 * and thus the jwt gets sent with the request as a cookie.
 *
 * @returns Promise
 */
export const getAdditionalAccountInfo = (): Promise<AccountWithAuthz> => {
  return axiosInstance.get("/accounts/me").then((res) => res.data);
};

/**
 * Endpoint for accessing the Accounts view in a Webmap Schema.
 * Holds all account information plus role and seclevel for a given webmap.
 */
class AccountsExtendedEndpoint {
  constructor(protected endpointPath: string) {}

  getAll(
    orderBy?: string,
    orderType?: GridSortDirection,
    limit?: number,
    offset?: number,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<AccountExtended[]>> {
    if (orderType === undefined) {
      orderType = "asc";
    }
    return axiosInstance.get(this.endpointPath + "/", {
      params: { orderBy, orderType, limit, offset },
      ...config,
    });
  }

  update(
    id: number,
    changes: Partial<AccountExtended>,
    config?: AxiosRequestConfig
  ): Promise<AccountExtended> {
    return axiosInstance
      .patch(`${this.endpointPath}/${id}`, changes, config)
      .then((res) => res.data);
  }
}

/**
 * Endpoint for accessing the webmap view in a WebMap schema. Only consists of
 * one entry (which refers to the webmap in use).
 */
class WebMapEndpoint {
  constructor(protected endpointPath: string) {}

  get(config?: AxiosRequestConfig): Promise<AxiosResponse<IWebMap>> {
    return axiosInstance.get(this.endpointPath + "/", config);
  }

  update(
    changes: Partial<IWebMap>,
    config?: AxiosRequestConfig
  ): Promise<IWebMap> {
    return axiosInstance
      .patch(`${this.endpointPath}/`, changes, config)
      .then((res) => res.data);
  }
}

export const AccountsExtended = new AccountsExtendedEndpoint("/meta/accounts");
export const WebmapEndpoint = new WebMapEndpoint("/meta/webmap"); // to avoid name clash
export const SecLevelsWebMap = new SecLevelEndpoint(
  "/meta/seclevels",
  axiosInstance
);
